import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Table from '@awp/components/table';
import { sortByNumericThenAlpha } from '@awp/helpers/sorter';
import { Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FORM_MODE, Permissions, REST_API } from '@awp/constants';
import { isValueEmpty } from '@awp/helpers/utilsReact';
import Authorize from '@awp/components/Authorize';
import { PutJson } from '@awp/helpers/fetchReact';
import { ActionMenuEdit, ActionMenuView } from '@awp/components/Common/AwpActionMenus';
import AwpToastContent from '@components/Common/AwpToastContent';
import Dropdown from '@awp/components/Common/DropDown/Dropdown';
import {
  getRegionLoading,
  getStatusLoading,
  getTypeLoading,
  selectAllRegions,
  selectAllWeatherStationStatuses,
  selectAllWeatherStationStatuseTypes,
  selectRegionId,
  selectWeatherStationStatusId,
  selectWeatherStationStatusTypeId,
  setRegion,
  setWeatherStationStatus,
  setWeatherStationStatusType,
} from '@stores/weatherStationStatusesSlice';
import { WeatherStationService } from '@components/WeatherStationAdmin/WeatherStationService';

const WeatherStationTable = ({
  isMedium,
  isLarge,
  data,
  setData,
  loading,
  setMode,
  mode,
  setWeatherStation,
  search,
  setSearch,
  defaultPageSize,
  setDefaultPageSize,
  filteredData,
  setFilteredData,
}) => {
  const allWeatherStationStatuses = useSelector(selectAllWeatherStationStatuses);
  const currentWeatherStationStatusId = useSelector(selectWeatherStationStatusId);
  const currentStatusLoading = useSelector(getStatusLoading);
  const allWeatherStationTypes = useSelector(selectAllWeatherStationStatuseTypes);
  const currentWeatherStationTypeId = useSelector(selectWeatherStationStatusTypeId);
  const currentTypeLoading = useSelector(getTypeLoading);
  const allRegions = useSelector(selectAllRegions);
  const currentRegionId = useSelector(selectRegionId);
  const currentRegionLoading = useSelector(getRegionLoading);
  const [statusOptions, setStatusOptions] = useState([]);
  const [updatingWeatherStation, setUpdatingWeatherStation] = useState(false);

  const crudService = new WeatherStationService(mode);

  const setCurrentWeatherStationStatus = (id) => async (dispatch) => {
    const status = allWeatherStationStatuses.find((x) => x.Id === id);
    dispatch(setWeatherStationStatus(status));
  };

  const setCurrentWeatherStationType = (id) => async (dispatch) => {
    const type = allWeatherStationTypes.find((x) => x.Id === id);
    dispatch(setWeatherStationStatusType(type));
  };

  const setCurrentRegion = (id) => async (dispatch) => {
    const region = allRegions.find((x) => x.Id === id);
    dispatch(setRegion(region));
  };

  const actionMenu = (row) => {
    return (
      <>
        <div>
          <Authorize requires={[Permissions.WriteWeatherStation]}>
            <ActionMenuEdit
              onClick={() => {
                setMode(FORM_MODE.EDIT);
                setWeatherStation(row.row.original);
              }}
            />
          </Authorize>
          <ActionMenuView
            onClick={() => {
              setMode(FORM_MODE.VIEW);
              setWeatherStation(row.row.original);
            }}
          />
        </div>
      </>
    );
  };

  const updateData = (wss) => {
    let _data = [...data];
    console.log(_data);
    console.log(wss);
    if (wss && wss.length > 0) {
      wss.map((ws) => {
        let index = data.findIndex((x) => x.Id === ws.Id);
        if (index >= 0) {
          _data[index] = ws;
        }
      });
      setData([..._data]);
    }
  };

  const getStatusOptions = () => {
    let opts = [];
    if (allWeatherStationStatuses && allWeatherStationStatuses.length > 0) {
      allWeatherStationStatuses.map((wss) => {
        opts.push({
          text: wss.Name,
          value: wss.Id,
        });
      });
    }
    setStatusOptions(opts);
  };

  const hasSearch = (src) => {
    return src ? src.toLowerCase().includes(search.toLowerCase()) : false;
  };

  useEffect(() => {
    if (!data || data.length === 0) return;
    if (statusOptions == [] || statusOptions.length < 1) getStatusOptions();
    let filtered = [...data];
    if (allWeatherStationTypes && currentWeatherStationTypeId > 0) {
      filtered = filtered.filter((wx) => wx.TypeId === currentWeatherStationTypeId);
    }

    if (allRegions && currentRegionId > 0) {
      filtered = filtered.filter((wx) => wx.ContractAreaId === currentRegionId);
    }
    const weatherStationStatus = allWeatherStationStatuses.find((wss) => wss.Id == currentWeatherStationStatusId);
    if (weatherStationStatus) {
      filtered = filtered.filter((wx) => wx.StatusTypeName === weatherStationStatus.Name);
    }
    if (!isValueEmpty(search)) {
      filtered = filtered.filter(
        (ws) =>
          hasSearch(ws.Number) ||
          hasSearch(ws.Name) ||
          hasSearch(ws.Description) ||
          (ws.ContractArea && !isValueEmpty(ws.ContractArea.Name) ? hasSearch(ws.ContractArea.Name) : false) ||
          (ws.Status && !isValueEmpty(ws.Status.Name) ? hasSearch(ws.Status.Name) : false) ||
          (ws.Type && !isValueEmpty(ws.Type.Name) ? hasSearch(ws.Type.Name) : false) ||
          (!isValueEmpty(ws.Longitude) ? hasSearch(ws.Longitude + '') : false) ||
          (!isValueEmpty(ws.Latitude) ? hasSearch(ws.Latitude + '') : false) ||
          (!isValueEmpty(ws.Elevation) ? hasSearch(ws.Elevation + '') : false),
      );
    }

    setFilteredData(filtered);
  }, [data, search, currentWeatherStationStatusId, currentWeatherStationTypeId, currentRegionId]);

  const columns = React.useMemo(
    () => [
      {
        Header: 'Number',
        accessor: 'Number',
        sortType: sortByNumericThenAlpha,
      },
      {
        Header: 'Name',
        accessor: 'Name',
        sortType: sortByNumericThenAlpha,
        Cell: (c) => {
          return <div className="text-wrap">{c.value}</div>;
        },
      },
      {
        Header: 'Type',
        accessor: 'TypeNameNonNull',
        show: isLarge,
        sortType: sortByNumericThenAlpha,
      },
      {
        Header: 'Status',
        accessor: 'StatusTypeName',
        show: isMedium,
        style: { width: '150px' },
        sortType: sortByNumericThenAlpha,
      },
      {
        Header: 'Contract Area',
        accessor: 'ContractAreaName',
        show: isLarge,
        sortType: (rowA, rowB) => {
          let valA = undefined;
          let valB = undefined;

          if (rowA.original.ContractArea && rowA.original.ContractArea.Number) {
            valA = rowA.original.ContractArea.Number;
          }

          if (rowB.original.ContractArea && rowB.original.ContractArea.Number) {
            valB = rowB.original.ContractArea.Number;
          }

          if (valA === valB) {
            return 0;
          }

          if (valA === undefined) {
            return -1;
          }

          if (valB === undefined) {
            return 1;
          }

          return valA > valB ? 1 : -1;
        },
      },
      {
        Header: 'Actions',
        accessor: 'Id',
        disableSortBy: true,
        Cell: actionMenu,
      },
    ],
    [isLarge, isMedium],
  );

  const selectableRows = {
    enabled: true,
    options: statusOptions,
    updateloading: updatingWeatherStation,
    handleChange: (statusValue, weatherStations) => {
      let url = REST_API.WEATHER_STATIONS + 'bulk';

      let requestData = {
        StatusId: statusValue,
        Ids: weatherStations.map((w) => w.original.Id),
      };
      setUpdatingWeatherStation(true);
      PutJson(url, requestData)
        .then(async (response) => {
          setUpdatingWeatherStation(false);
          if (response.ok) {
            const wss = await response.json();
            console.log(wss);
            updateData(wss);
            let msgs = [];
            if (wss && wss.length > 0) {
              wss.map((ws) => {
                msgs.push(`${ws.Number} - ${ws.Name}`);
              });
            }

            toast(<AwpToastContent title={'Successfully updated weather stations:'} msgList={msgs} isErr={false} />, {
              autoClose: false,
            });
          } else if (response.status === 412) {
            const errors = await response.json();
            //setErrors(errors);
            //if (errors.entity) setEntityErrors({ WeatherStation: errors.entity });
          }
        })
        .finally(() => {
          //setSubmitting(false);
        });
    },
  };
  return (
    <>
      <div className="container mb-3">
        <div className="row">
          <div className="col-sm-8 pl-0">{crudService.crud.getHeader()}</div>
        </div>
      </div>
      <div className="container mb-3">
        <div className="row">
          <div className="col-sm-3">
            <Form.Label>
              <b>{'Search'}</b>
            </Form.Label>
            <input
              className="form-control  float-left"
              placeholder="Search..."
              id="user_text_search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            ></input>
          </div>
          <div className="col-sm-3">
            <Dropdown
              ChangeHandler={setCurrentWeatherStationType}
              AllOptions={selectAllWeatherStationStatuseTypes}
              CurrentValue={selectWeatherStationStatusTypeId}
              LoadingStatus={currentTypeLoading}
              Title={'Type'}
            />
          </div>
          <div className="col-sm-3">
            <Dropdown
              ChangeHandler={setCurrentWeatherStationStatus}
              AllOptions={selectAllWeatherStationStatuses}
              CurrentValue={selectWeatherStationStatusId}
              LoadingStatus={currentStatusLoading}
              Title={'Status'}
            />
          </div>
          <div className="col-sm-3 pr-0">
            <Dropdown
              ChangeHandler={setCurrentRegion}
              AllOptions={selectAllRegions}
              CurrentValue={selectRegionId}
              LoadingStatus={currentRegionLoading}
              Title={'Contract Area'}
            />
          </div>
        </div>
      </div>
      <Table
        columns={columns}
        data={filteredData}
        fetchData={() => {}}
        loading={loading}
        search=""
        defaultSortBy="Number"
        selectableRows={selectableRows}
        defaultPageSize={defaultPageSize}
        setDefaultPageSize={setDefaultPageSize}
      />
      <div className="container mb-3" style={{ paddingTop: '1.25rem' }}>
        <div className="row">
          <div className="col-md-12 col-lg-12 col-sm-12 pr-0 pl-0">
            <Authorize requires={[Permissions.WriteWeatherStation]}>
              <Button
                className="mr-2 mr-mini-0 float-left"
                variant="primary"
                onClick={() => {
                  setMode(FORM_MODE.CREATE);
                }}
                disabled={loading}
              >
                Create
              </Button>
            </Authorize>
          </div>
        </div>
      </div>
    </>
  );
};

export default WeatherStationTable;
